import { useCallback, useEffect, useState } from 'react';
import getUserProfiles from 'api/Sagemaker/getUserProfiles';
import useClient from 'api/client';
import {
    DeleteSagemakerUserProfileInput,
    Environment,
    NewSagemakerUserProfileInput,
    UserProfilesMessage,
} from 'types/Environment';
import deleteSmUserProfile from 'api/Sagemaker/deleteSmUserProfile';
import { useAlert } from 'components/message/AlertContext';
import generatePresignedUrl from 'api/Sagemaker/generatePresignedUrl';
import createSmUserProfile from 'api/Sagemaker/createSmUserProfile';

type GroupType = 'Admin' | 'FullDev' | 'BasicDev' | 'Reader';
type LoadingType = 'deleteLoading' | 'urlLoading';

export default function useUserProfiles(environment: Environment) {
    const client = useClient();
    const { openAlert } = useAlert();

    const [userProfiles, setUserProfiles] = useState<UserProfilesMessage[]>([]);
    const [loadingUserProfiles, setLoadingUserProfiles] = useState(true);

    const updateUserProfileLoading = (
        group: string,
        role: string,
        loadingType: LoadingType,
        isLoading: boolean,
    ) => {
        setUserProfiles((prevUserProfiles) => {
            // Find the index of the user profile in the array based on group, username, and role
            const index = prevUserProfiles.findIndex(
                (profile) => profile.group === group && profile.role === role,
            );

            // If the user profile is found, update the loading value
            if (index !== -1) {
                return prevUserProfiles.map((profile, i) =>
                    i === index
                        ? { ...profile, [loadingType]: isLoading }
                        : profile,
                );
            }

            // Return the original array if the user profile is not found
            return prevUserProfiles;
        });
    };

    const generateUrl = async (
        group: string,
        username: string,
        role: string,
    ) => {
        updateUserProfileLoading(group, role, 'urlLoading', true);
        if (environment.environmentUri) {
            const input: NewSagemakerUserProfileInput = {
                environmentUri: environment.environmentUri ?? '',
                group,
                role,
                userProfileParamsJson: username,
            };
            await client
                ?.query(generatePresignedUrl(input))
                .then((response) => {
                    const parsedResponse = JSON.parse(
                        response.data.generatePresignedUrl,
                    );
                    if (parsedResponse.status !== 'error') {
                        window.open(parsedResponse.message, '_blank');
                    } else {
                        openAlert({
                            severity: 'alert',
                            message: parsedResponse.message,
                        });
                    }
                });
        }
        updateUserProfileLoading(group, role, 'urlLoading', false);
    };

    const deleteUserProfile = useCallback(
        async (group: string, role: string) => {
            updateUserProfileLoading(group, role, 'deleteLoading', true);
            if (client && environment.environmentUri) {
                const input: DeleteSagemakerUserProfileInput = {
                    environmentUri: environment.environmentUri,
                    group,
                    role,
                };
                const response = await client.mutate(
                    deleteSmUserProfile(input),
                );
                const parsedResponse = JSON.parse(
                    response?.data.deleteSmUserProfile,
                );
                if (parsedResponse.status !== 'error') {
                    setUserProfiles((prevUserProfiles) =>
                        prevUserProfiles.filter(
                            (profile) =>
                                profile.group !== group ||
                                profile.role !== role,
                        ),
                    );
                    openAlert({
                        message: 'User Profile deleted successfully',
                        severity: 'success',
                    });
                } else {
                    openAlert({
                        message: parsedResponse.message,
                        severity: 'alert',
                    });
                }
            }
            updateUserProfileLoading(group, role, 'deleteLoading', false);
        },
        [client, environment],
    );

    function getOriginalGroup({
        group,
        role,
        username,
    }: UserProfilesMessage): UserProfilesMessage {
        const originGroups: [string[], GroupType][] = [
            [environment.originSamlGroupName, 'Admin'],
            [environment.originSamlFullDevGroupName, 'FullDev'],
            [environment.originSamlBasicDevGroupName ?? [], 'BasicDev'],
            [environment.originSamlReaderGroupName, 'Reader'],
        ];

        let originalGroup: string | null = null;
        let groupType: GroupType | null = null;

        for (const [groupArray, groupTypeValue] of originGroups) {
            for (const item of groupArray) {
                const transformedSearchString = item.replace(/_/g, '');
                if (
                    transformedSearchString.toLowerCase() ===
                    group.toLowerCase()
                ) {
                    originalGroup = item;
                    groupType = groupTypeValue;
                    break;
                }
            }
            if (originalGroup !== null && groupType !== null) {
                break;
            }
        }

        return {
            username,
            group: originalGroup || group,
            role: groupType || role,
            deleteLoading: false,
            urlLoading: false,
        };
    }

    const createUserProfile = async (role: string, group: string) => {
        if (environment.environmentUri && client) {
            const response = await client.mutate(
                createSmUserProfile({
                    environmentUri: environment.environmentUri,
                    role,
                    group,
                }),
            );

            const parsedResponse = JSON.parse(
                response?.data.createSmUserProfile,
            );

            if (!response.errors && parsedResponse.status !== 'error') {
                openAlert({
                    message: `User Profile created successfully`,
                    severity: 'success',
                });
                setUserProfiles([
                    ...userProfiles,
                    getOriginalGroup({
                        group,
                        role,
                        username: parsedResponse.message,
                    }),
                ]);
                return '';
            } else if (response.errors) {
                return response.errors[0].message;
            } else {
                return parsedResponse.message;
            }
        }
    };

    useEffect(() => {
        const fetchData = async () => {
            setLoadingUserProfiles(true);
            if (client && environment.environmentUri) {
                try {
                    const response = await client.query(
                        getUserProfiles(environment.environmentUri),
                    );
                    const parsedResponse = JSON.parse(
                        response.data.getUserProfiles,
                    );
                    if (parsedResponse.status !== 'error') {
                        setUserProfiles(
                            JSON.parse(parsedResponse.message).map(
                                (item: UserProfilesMessage) =>
                                    getOriginalGroup(item),
                            ),
                        );
                    }
                } catch (error) {
                    openAlert({
                        message:
                            'Something went wrong... Could not retrieve user profiles',
                        severity: 'alert',
                    });
                } finally {
                    setLoadingUserProfiles(false);
                }
            }
        };

        if (client) {
            fetchData();
        }
    }, [client, environment]);
    return {
        userProfiles,
        loadingUserProfiles,
        generateUrl,
        deleteUserProfile,
        createUserProfile,
    };
}
